import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import type {
  ArrayPath,
  Control,
  DefaultValues,
  FieldArray,
  FieldErrors,
  FormState,
  Path,
  Resolver,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormRegister
} from 'react-hook-form';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import get from 'lodash/get';
import styled from 'styled-components';
import { zodResolver } from '@hookform/resolvers/zod';

import { errorToMessageString, mapCellTypeToInputType } from '../../common';
import type { TTableCellContent } from '../atoms';

import {
  ArrayInputWrapper,
  ButtonContainer,
  InputWrapper,
  Modal,
  PrimaryButton,
  ProcessedMessage,
  SecondaryButton,
  SectionPreTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';
import type {
  AccountProductBlockRow,
  ArrayAccountProductBlockRow
} from '../common/types/Product';
import { cloneDeep } from 'lodash';
import type { PromiseOr } from 'simplytyped';
import type { z } from 'zod';

export type EditSchemaModalProps<Schema extends z.ZodTypeAny = z.ZodSchema> = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  schema: Schema;
  data: z.infer<Schema>;
  setUpdateSuccessful?: Dispatch<SetStateAction<boolean>>;
  columns: ReadonlyArray<ReadonlyArray<AccountProductBlockRow>>;
  itemTitle: string;
  itemSubtitle?: string;
  parseData?: (data: z.infer<Schema>) => z.infer<Schema>;
  onSubmit: (args: {
    data: z.infer<Schema>;
    formState: FormState<Schema>;
  }) => PromiseOr<unknown>;
};

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

const isArrayRow = (
  row: AccountProductBlockRow
): row is ArrayAccountProductBlockRow => {
  return row.contentType === 'array';
};

const isRowVisible = (row: AccountProductBlockRow): boolean => {
  return !!row.name && !row.nonEditable && !row.hidden;
};

const RowInputHandler = <Schema extends z.ZodTypeAny>(props: {
  row: AccountProductBlockRow;
  control: Control<Schema>;
  errors: FieldErrors<Schema>;
  register: UseFormRegister<Schema>;
}) => {
  const {
    row: { title, content: _content, name, contentType, editingOptions = {} },
    control,
    errors,
    register
  } = props;

  if (!isRowVisible(props.row)) {
    return null;
  }

  const content = typeof _content === 'function' ? _content() : _content;

  if (isArrayRow(props.row)) {
    return (
      <ArrayInputWrapper<Schema>
        name={name as ArrayPath<Schema>}
        type={mapCellTypeToInputType({
          contentType: props.row.itemContentType,
          content: props.row.defaultValue
        } as TTableCellContent)}
        label={title}
        register={register}
        errors={
          ((name || '') in errors
            ? errors[name as keyof typeof errors]
            : undefined) as any
        }
        control={control}
        defaultValue={
          props.row.defaultValue as FieldArray<Schema, ArrayPath<Schema>>
        }
      />
    );
  }

  const errorMessage = errorToMessageString(
    get(errors, name as ArrayPath<Schema>, '')
  );

  const type = mapCellTypeToInputType({
    contentType: contentType,
    content: content
  } as TTableCellContent);

  if (type === 'table') {
    throw new Error('Table type not supported in EditProductModal');
  }

  return (
    <InputWrapper
      name={name as Path<Schema>}
      type={type}
      label={title}
      register={register}
      errorMessage={errorMessage}
      {...editingOptions}
    />
  );
};

export const EditSchemaModal = <Schema extends z.ZodTypeAny>({
  isModalOpen,
  handleModalClose,
  data,
  setUpdateSuccessful = () => null,
  columns,
  itemSubtitle,
  schema,
  onSubmit,
  parseData = (arg) => arg,
  itemTitle
}: EditSchemaModalProps<Schema>) => {
  const [successfullyProcessed, setSuccessfullyProcessed] = useState<
    null | boolean
  >(null);
  const defaultValues = cloneDeep(data);

  // need to conditionally massage defaults prior to zod validation
  const zResolver: Resolver<Schema> = zodResolver(schema);
  const resolver: Resolver<Schema> = async (data, ...args) => {
    // make sure fee defaults are set
    const values = parseData(data);
    // let zod do it's thing
    return zResolver(values as typeof schema, ...args);
  };
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
    formState
  } = useForm<Schema>({
    resolver,
    defaultValues: defaultValues as DefaultValues<Schema>
  });

  const submitHandler: SubmitHandler<Schema> = async (data) => {
    setUpdateSuccessful(false);

    // TODO log
    //console.log(JSON.stringify(data, null, 2));

    try {
      await onSubmit({ data, formState });

      toast.success(`Successfully updated ${itemTitle}`);

      setSuccessfullyProcessed(true);
      setUpdateSuccessful(true);
    } catch (err) {
      toast.error(errorToMessageString(err));
      setSuccessfullyProcessed(false);
      setUpdateSuccessful(false);
    }
  };

  const handleReset = () => {
    setSuccessfullyProcessed(null);
    reset();
    handleModalClose();
  };

  const onSubmitError: SubmitErrorHandler<Schema> = (err) => {
    console.error(err);
    toast.error('Something went wrong while trying to submit the form.');
  };

  return (
    <Modal handleClose={handleReset} isModalVisible={isModalOpen}>
      <SectionPreTitle>{itemTitle}</SectionPreTitle>
      <SectionTitle>Edit {itemSubtitle}</SectionTitle>
      {successfullyProcessed === null && (
        <form onSubmit={handleSubmit(submitHandler, onSubmitError)}>
          <SectionWrapper>
            {columns
              .flatMap((columns) => columns)
              .filter((row) => isRowVisible(row))
              .map((row) => (
                <RowInputHandler<Schema>
                  key={`${row.name}-${row.title}`}
                  row={row}
                  control={control}
                  errors={errors}
                  register={register}
                />
              ))}
          </SectionWrapper>
          <FormFooter
            horizontalSpacing="10px"
            verticalSpacing="0px"
            justify="end"
          >
            <SecondaryButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
              disabled={isSubmitting}
            >
              <span>Cancel</span>
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              <span>Save {itemTitle}</span>
            </PrimaryButton>
          </FormFooter>
        </form>
      )}
      {successfullyProcessed === true && (
        <ProcessedMessage heading="Success">
          <p>Update was successfully made.</p>
        </ProcessedMessage>
      )}
      {successfullyProcessed === false && (
        <ProcessedMessage error={true} heading="Error">
          <p>Something went wrong when updating</p>
        </ProcessedMessage>
      )}
    </Modal>
  );
};
